import { IButtonStyles } from '@fluentui/react/lib/Button';

import { addAlpha } from '../../util/colors';
import { BorderRadius } from '../../util/styles/borders';
import { getThemeFontSizeStyles } from '../../util/styles/fonts';
import { buttonTransitionStyle } from '../../util/styles/transitions';
import { IRawStyle, IStyle, ITheme, getFocusStyle, memoizeFunction } from '../../util/styling';
import { tokens } from '../../util/theme/fluent9';

import { ButtonColor, ButtonStatus } from './Button.types';

type GetButtonColors = (color: ButtonColor, theme: ITheme) => IButtonStyles;
export const getButtonColors: GetButtonColors = (color, theme) => colorStylesMap[color](theme);

const getPrimaryColorStyles = (theme: ITheme) => ({
  root: {
    color: theme.semanticColors.primaryButtonText,
    backgroundColor: theme.semanticColors.primaryButtonBackground,
    borderColor: theme.semanticColors.primaryButtonBackground,
    textDecoration: 'none',
  },
  rootHovered: {
    color: theme.semanticColors.primaryButtonTextHovered,
    backgroundColor: theme.semanticColors.primaryButtonBackgroundHovered,
    borderColor: theme.semanticColors.primaryButtonBackgroundHovered,
    textDecoration: 'none',
  },
  rootPressed: {
    color: theme.semanticColors.primaryButtonTextPressed,
    backgroundColor: theme.semanticColors.primaryButtonBackgroundPressed,
    borderColor: theme.semanticColors.primaryButtonBackgroundPressed,
  },
  rootDisabled: {
    color: theme.semanticColors.primaryButtonTextDisabled,
    backgroundColor: theme.semanticColors.primaryButtonBackgroundDisabled,
    borderColor: theme.semanticColors.primaryButtonBackgroundDisabled,
  },
});

const getSecondaryColorStyles = (theme: ITheme) => ({
  root: {
    color: theme.semanticColors.buttonText,
    backgroundColor: theme.semanticColors.buttonBackground,
    borderColor: theme.semanticColors.buttonBorder,
    textDecoration: 'none',
  },
  rootHovered: {
    color: theme.semanticColors.buttonTextHovered,
    backgroundColor: theme.semanticColors.buttonBackgroundHovered,
    borderColor: theme.semanticColors.buttonBorder,
    textDecoration: 'none',
  },
  rootPressed: {
    color: theme.semanticColors.buttonTextPressed,
    backgroundColor: theme.semanticColors.buttonBackgroundPressed,
    borderColor: theme.semanticColors.buttonBorder,
  },
  rootDisabled: {
    color: theme.semanticColors.buttonTextDisabled,
    backgroundColor: theme.semanticColors.buttonBackgroundDisabled,
    borderColor: theme.semanticColors.buttonBorderDisabled,
  },
});

const getTertiaryColorStyles = (theme: ITheme): IButtonStyles => {
  const secondaryStyles = getSecondaryColorStyles(theme);
  const backgroundColor = addAlpha(secondaryStyles.root.backgroundColor, 90);
  const hoveredBackgroundColor = addAlpha(secondaryStyles.rootHovered.backgroundColor, 90);
  const pressedBackgroundColor = addAlpha(secondaryStyles.rootPressed.backgroundColor, 90);
  const disabledBackgroundColor = addAlpha(secondaryStyles.rootDisabled.backgroundColor, 90);

  return {
    root: {
      ...secondaryStyles.root,
      backgroundColor,
      borderColor: backgroundColor,
    },
    rootHovered: {
      backgroundColor: hoveredBackgroundColor,
      borderColor: hoveredBackgroundColor,
    },
    rootPressed: {
      backgroundColor: pressedBackgroundColor,
      borderColor: pressedBackgroundColor,
    },
    rootDisabled: {
      backgroundColor: disabledBackgroundColor,
      borderColor: disabledBackgroundColor,
    },
  };
};

type GetColorStyles = (theme: ITheme) => IButtonStyles;
const colorStylesMap: Record<ButtonColor, GetColorStyles> = {
  primary: getPrimaryColorStyles,
  secondary: getSecondaryColorStyles,
  tertiary: getTertiaryColorStyles,
};

interface GetRootButtonStyleProps {
  readonly fontSize: IRawStyle['fontSize'];
  readonly fullWidth: boolean;
  readonly lineHeight?: IRawStyle['lineHeight'];
  readonly status: ButtonStatus;
  readonly hasText: boolean;
  readonly borderRadius: BorderRadius;
}

type GetRootButtonStyles = (props: GetRootButtonStyleProps) => IStyle;
export const getRootButtonStyle: GetRootButtonStyles = ({
  fontSize,
  lineHeight,
  status,
  fullWidth,
  hasText,
  borderRadius,
}) => ({
  borderRadius,
  fontSize,
  fontWeight: tokens.fontWeightSemibold,
  lineHeight,
  textAlign: 'center',
  display: fullWidth ? 'block' : 'inline-block',
  position: 'relative',
  whiteSpace: 'nowrap',
  transition: buttonTransitionStyle,
  borderWidth: '1px',
  borderStyle: 'solid',
  padding: hasText ? '5px 12px' : '5px 8px',
  cursor: status === 'enabled' ? 'pointer' : 'default',
  width: fullWidth ? '100%' : undefined,
});
export interface BaseButtonStyleProps {
  readonly theme: ITheme;
  readonly color: ButtonColor;
  readonly fullWidth: boolean;
  readonly status: ButtonStatus;
  readonly hasText: boolean;
  readonly borderRadius: BorderRadius;
}

type GetButtonStyles = (props: BaseButtonStyleProps) => IButtonStyles;
export const getButtonStyles: GetButtonStyles = (props) =>
  getBaseButtonStylesMemoized(
    props.theme,
    props.color,
    props.fullWidth,
    props.status,
    props.hasText,
    props.borderRadius
  );

const getBaseButtonStylesMemoized = memoizeFunction(
  (
    theme: ITheme,
    color: ButtonColor,
    fullWidth: boolean,
    status: ButtonStatus,
    hasText: boolean,
    borderRadius: BorderRadius
  ): IButtonStyles => {
    const colors = getButtonColors(color, theme);
    const { root, rootHovered, rootPressed, rootDisabled } = colors;

    const focusOutlineStyles = getFocusStyle(theme, { inset: -1 });
    const { fontSize, lineHeight } = getThemeFontSizeStyles('mediumSub', theme);
    const rootButtonStyle = getRootButtonStyle({ fontSize, lineHeight, fullWidth, status, hasText, borderRadius });

    return {
      root: [root, rootButtonStyle, focusOutlineStyles],
      rootHovered,
      rootPressed,
      rootDisabled,
    };
  }
);
